import { Content } from "pdfmake/interfaces";
import { LegendItem } from "helpers/models";
import layout from "report/common/layout";
import getTitle from "report/common/title";
import getLayer from "report/common/layer";
import getRect from "report/common/rect";
import getColor from "report/common/color";
import getOutside from "report/common/outside";
import getMatchingTitle from "components/fathomAnalyze/util";
import { PolygonLayerStatistic } from "../types";

const exclude = ["No Data", "No_Data", "Outside", "No Observation"];
const grey = ["No Data", "No_Data", "Outside", "No Observation", "Waterbody"];

const toDisplayNumber = (value: number, digits: number) => {
  return String(parseFloat(value.toFixed(digits)));
};

const formatP90Intensity = (value?: number) => {
  const safeValue = Number(value || 0);
  return toDisplayNumber(safeValue, 4);
};

const resolveSummaryMetrics = (metrics: PolygonLayerStatistic["metrics"]) => {
  if (!metrics) {
    return undefined;
  }

  const bundle = metrics as any;
  const hasBundledMetrics = !!bundle.intensityMetrics || !!bundle.detailMetrics;

  if (!hasBundledMetrics) {
    return metrics;
  }

  return bundle.intensityMetrics || bundle.detailMetrics;
};

export const orderLayerStatisticsByLegend = (
  layerStatistics: PolygonLayerStatistic[],
  legendItems: LegendItem[]
): PolygonLayerStatistic[] => {
  if (!legendItems.length) {
    return layerStatistics;
  }

  return legendItems
    .map((legend) =>
      layerStatistics.find((stat) => stat.layerId === legend.layerId)
    )
    .filter((stat): stat is PolygonLayerStatistic => stat !== undefined);
};

/**
 * 生成灾害分析概览表
 * 样式对齐单点分析概览：灾害图标+名称、颜色条、风险等级文字
 */
const getHazardOverview = (
  t: any,
  layerStatistics: PolygonLayerStatistic[],
  legendItems: LegendItem[]
): Content[] => {
  if (!layerStatistics || layerStatistics.length === 0) {
    return [];
  }

  const legendMap = new Map(legendItems.map((leg) => [leg.layerId, leg]));
  const isGreyLegendBar = (label?: string) =>
    grey.includes(String(label || "").trim());

  // 对齐 dashboard 展示顺序：按 legendItems 顺序重排统计项
  const orderedLayerStatistics = orderLayerStatisticsByLegend(
    layerStatistics,
    legendItems
  );

  const row = orderedLayerStatistics.map((stat) => {
    const legend = legendMap.get(stat.layerId);
    const summaryMetrics = resolveSummaryMetrics(stat.metrics);
    const p90Label = summaryMetrics?.p90ValueLabel || "";
    const p90Intensity = Number(summaryMetrics?.p90 || 0);
    const p90IntensityValue = stat.layerId.includes("Fathom")
      ? ""
      : p90Intensity === 0
        ? "1"
        : formatP90Intensity(p90Intensity);

    if (!legend || !legend.rules || !legend.rules.length) {
      return [];
    }

    const rules = legend.rules.filter((r) => !exclude.includes(r.title));

    const index = rules.findIndex(
      (item) =>
        item.title === p90Label ||
        item.title ===
          getMatchingTitle(p90Label.replace("years", "").replace(" ", ""))
    );

    const colorArray = getColor(rules);
    const fillColor = p90Label === "Waterbody" ? "#DCDBDC" : "#D3D3D3";

    return [
      getLayer(t, stat.layerId),
      isGreyLegendBar(p90Label)
        ? getOutside(fillColor)
        : getRect(colorArray, index),
      p90Label ? t("legend." + p90Label) : "",
      p90IntensityValue
    ];
  });

  return [
    getTitle(t("report.Analysis Overview")),
    {
      table: {
        widths: [160, 235, 75, 95],
        body: [
          [
            { text: t("report.hazard") },
            { text: t("fathom.Hazard Risk") },
            { text: "" },
            { text: t("drawShape.P90 Intensity") }
          ],
          ...row
        ]
      },
      layout: layout(row.length + 2, false, 3),
      fontSize: 8,
      opacity: 0.6,
      lineHeight: 1,
      characterSpacing: 0
    }
  ];
};

export default getHazardOverview;
